import { ReloadOutlined } from "@ant-design/icons";
import { DatePicker } from "antd";
import { getApi } from "apis";
import DashboardCard from "components/Global/DashBoardCard";
import DashboardSelectorCard from "components/Global/DashboardSelectorCard";
import { categoriesColumns, usersColumns, usersColumnsForQC, imageManagementColumns, mappedItemsColumns } from "constants/Data";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { checkPermission } from "utils/utils";
import { CategoriesCovered, DashBoardUser, TotalProductsAdded } from "utils/svgs";
import { toastText } from "utils/utils";
import styles from "./index.module.scss";
import ChartWithRangeSelector from "components/Global/DashboardCharts";

const { RangePicker } = DatePicker;

type CategoryData = {
  categoryId: string;
  categoryName: string;
  count: number;
};

type UserData = {
  id: string;
  fullName: string;
  total: number;
  approvedEntry?: number;
  qcCount?: number;
  imageCount?: number;
  mappedCount?: number;
};

type ChartDatum = {
  date: string;
  value: number;
};

export type DashboardPanel = 'medicine' | 'grocery';

type DashboardTableProps = {
  panel?: DashboardPanel;
};

const DashboardTable = ({ panel = 'medicine' }: DashboardTableProps) => {
  const isGroceryPanel = panel === 'grocery';
  const userProfile = useSelector((state: any) => state?.userProfile?.data);
  const isDashboardViewAll = useMemo(
    () =>
      checkPermission(userProfile?.permissions, {
        permissionName: "Dashboard",
        permission: ["viewAll"],
      }),
    [userProfile?.permissions]
  );

  const isCategoriesPermission = useMemo(
    () =>
      checkPermission(userProfile?.permissions, {
        permissionName: isGroceryPanel ? "GroceryCategories" : "Categories",
        permission: ["viewAll"],
      }),
    [userProfile?.permissions, isGroceryPanel]
  );

  const isUsersPermission = useMemo(
    () =>
      checkPermission(userProfile?.permissions, {
        permissionName: "Users",
        permission: ["view"],
      }),
    [userProfile?.permissions]
  );

  const [selectedDate, setSelectedDate] = useState<[Dayjs, Dayjs] | null>(null);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [categoryData, setCategoryData] = useState<CategoryData[]>([]);
  const [userData, setUserData] = useState<UserData[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [selectedQCUserIds, setSelectedQCUserIds] = useState<string[]>([]);
  const [selectedImageUserIds, setSelectedImageUserIds] = useState<string[]>([]);
  const [selectedMappedUserIds, setSelectedMappedUserIds] = useState<string[]>([]);
  const [imageManagementData, setImageManagementData] = useState<UserData[]>([]);
  const [mappedItemsData, setMappedItemsData] = useState<UserData[]>([]);
  const [, setImageCategoriesData] = useState<CategoryData[]>([]);
  const [chartRange, setChartRange] = useState<number>(7);
  const [chartData, setChartData] = useState<ChartDatum[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchDashboardSummary = useCallback(async (range?: number) => {
    try {
      
      setIsLoading(true);
      const params: any = {
        days: range || chartRange,
      };

      if (selectedDate) {
        params.selectedDate = selectedDate.map((date) => date.toISOString());
      }

      const summaryPath = isGroceryPanel
        ? "/dashboard/grocery-summary"
        : "/dashboard/summary";
      const res = await getApi(summaryPath, params);
      // axios response shape: { data: { data: payload, message } }
      const payload = res?.data?.data;

      const totals = payload?.totals || {};
      const users = payload?.users || {};
      const categories = payload?.categories || {};
      const medicines = payload?.medicines || {};
      const imageManagement = payload?.imageManagement || {};
      const mappedItems = payload?.mappedItems || {};

      const userList = users?.list || [];
      const filteredUsers = isDashboardViewAll
        ? userList
        : userList.filter((user: any) => user?.id === userProfile?.id);

      const computedProducts = isDashboardViewAll
        ? totals?.products || 0
        : filteredUsers.reduce(
            (acc: number, item: any) => acc + Number(item?.total || 0),
            0
          );

      setTotalProducts(computedProducts);
      setTotalUsers(isDashboardViewAll ? totals?.users || 0 : filteredUsers.length);
      setUserData(filteredUsers);
      setCategoryData(categories?.list || []);
      setChartData(medicines?.entriesByDate || []);

      // Image Management data
      const imageUserList = imageManagement?.users || [];
      const filteredImageUsers = isDashboardViewAll
        ? imageUserList
        : imageUserList.filter((user: any) => user?.id === userProfile?.id);
      setImageManagementData(filteredImageUsers);
      setImageCategoriesData(imageManagement?.categories || []);

      const mappedUserList = (mappedItems?.users || []).filter(
        (user: any) => Number(user?.mappedCount || 0) > 0
      );
      const filteredMappedUsers = isDashboardViewAll
        ? mappedUserList
        : mappedUserList.filter((user: any) => user?.id === userProfile?.id);
      setMappedItemsData(isGroceryPanel ? [] : filteredMappedUsers);

      setLastUpdated(dayjs().format("hh:mm a"));
    } catch (error: any) {
      toastText(error?.response?.data?.error?.errorDescription, "error");
    } finally {
      setIsLoading(false);
    }
  }, [chartRange, isDashboardViewAll, selectedDate, userProfile?.id, isGroceryPanel]);

  useEffect(() => {
    fetchDashboardSummary();
  }, [fetchDashboardSummary]);

  const calculateSum = (ids: string[], data: any[], idKey: string, valueKey: string) => {
    return data
      .filter((item) => ids.includes(item[idKey]))
      .reduce((acc, item) => acc + Number(item[valueKey] || 0), 0);
  };

  const handleCategoryChange = (value: string[]) => setSelectedCategoryIds(value);
  const handleUserChange = (value: string[]) => setSelectedUserIds(value);
  const handleQCUserChange = (value: string[]) => setSelectedQCUserIds(value);
  const handleImageUserChange = (value: string[]) => setSelectedImageUserIds(value);
  const handleMappedUserChange = (value: string[]) => setSelectedMappedUserIds(value);

  const handleRangeChange = (range: number) => {
    setChartRange(range);
    fetchDashboardSummary(range);
  };

  const selectedCategorySum = useMemo(() =>
    calculateSum(selectedCategoryIds, categoryData, "categoryId", "count"),
    [categoryData, selectedCategoryIds]
  );

  // Filter users with QC permission
  const qcUsersData = useMemo(() =>
    userData.filter((user: any) => user.hasQCPermission === true),
    [userData]
  );

  const selectedQcUserSum = useMemo(() =>
    calculateSum(selectedQCUserIds, qcUsersData, "id", "qcCount"),
    [qcUsersData, selectedQCUserIds]
  );

  const selectedUserSum = useMemo(() =>
    calculateSum(selectedUserIds, userData, "id", "total"),
    [userData, selectedUserIds]
  );

  const selectedUserApprovedMedicines = useMemo(() =>
    calculateSum(selectedUserIds, userData, "id", "approvedEntry"),
    [userData, selectedUserIds]
  );

  const selectedUserQcCount = useMemo(() =>
    calculateSum(selectedUserIds, userData, "id", "qcCount"),
    [userData, selectedUserIds]
  );

  const selectedImageUserSum = useMemo(() =>
    calculateSum(selectedImageUserIds, imageManagementData, "id", "imageCount"),
    [imageManagementData, selectedImageUserIds]
  );

  const selectedMappedUserSum = useMemo(() =>
    calculateSum(selectedMappedUserIds, mappedItemsData, "id", "mappedCount"),
    [mappedItemsData, selectedMappedUserIds]
  );

  const cardData = [
    { icon: <TotalProductsAdded />, title: "Total Products Added", value: totalProducts, isClickable: false },
    { 
      icon: <CategoriesCovered />, 
      title: "Total Categories Covered", 
      value: categoryData.length,
      isClickable: isCategoriesPermission,
      route: isGroceryPanel ? "/grocery-categories" : "/categories"
    },
    { 
      icon: <DashBoardUser />, 
      title: "Total Users", 
      value: totalUsers,
      isClickable: isUsersPermission,
      route: "/settings/users"
    },
  ];

  // Helper function to sort options with selected items at the top
  const sortOptionsWithSelectedFirst = (options: any[], selectedIds: string[]) => {
    if (!selectedIds || selectedIds.length === 0) {
      return options;
    }
    
    const selectedOptions = options.filter(option => selectedIds.includes(option.value));
    const unselectedOptions = options.filter(option => !selectedIds.includes(option.value));
    
    // Maintain the order of selected items as they appear in selectedIds
    const orderedSelectedOptions = selectedIds
      .map(id => selectedOptions.find(opt => opt.value === id))
      .filter(Boolean);
    
    return [...orderedSelectedOptions, ...unselectedOptions];
  };

  // Helper function to sort table data with selected items at the top
  const sortTableDataWithSelectedFirst = (data: any[], selectedIds: string[], keyField: string = 'key') => {
    if (!selectedIds || selectedIds.length === 0) {
      return data;
    }
    
    const selectedData = data.filter(item => selectedIds.includes(item[keyField]));
    const unselectedData = data.filter(item => !selectedIds.includes(item[keyField]));
    
    // Maintain the order of selected items as they appear in selectedIds
    const orderedSelectedData = selectedIds
      .map(id => selectedData.find(item => item[keyField] === id))
      .filter(Boolean);
    
    return [...orderedSelectedData, ...unselectedData];
  };

  const categoryOptions = useMemo(() => {
    const options = categoryData.map(({ categoryId, categoryName }) => ({ label: categoryName, value: categoryId }));
    return sortOptionsWithSelectedFirst(options, selectedCategoryIds);
  }, [categoryData, selectedCategoryIds]);

  const userOptions = useMemo(() => {
    const options = userData.map(({ id, fullName }) => ({ label: fullName, value: id }));
    return sortOptionsWithSelectedFirst(options, selectedUserIds);
  }, [userData, selectedUserIds]);

  const qcUserOptions = useMemo(() => {
    const options = qcUsersData.map(({ id, fullName }) => ({ label: fullName, value: id }));
    return sortOptionsWithSelectedFirst(options, selectedQCUserIds);
  }, [qcUsersData, selectedQCUserIds]);

  const imageUserOptions = useMemo(() => {
    const options = imageManagementData.map(({ id, fullName }) => ({ label: fullName, value: id }));
    return sortOptionsWithSelectedFirst(options, selectedImageUserIds);
  }, [imageManagementData, selectedImageUserIds]);

  const mappedUserOptions = useMemo(() => {
    const options = mappedItemsData.map(({ id, fullName }) => ({ label: fullName, value: id }));
    return sortOptionsWithSelectedFirst(options, selectedMappedUserIds);
  }, [mappedItemsData, selectedMappedUserIds]);

  const tableCategoryData = useMemo(() => {
    const data = categoryData.map(({ categoryId, categoryName, count }) => ({
      key: categoryId,
      category: categoryName,
      entries: count,
    }));
    return sortTableDataWithSelectedFirst(data, selectedCategoryIds);
  }, [categoryData, selectedCategoryIds]);

  const tableUserData = useMemo(() => {
    const data = userData.map((item: any) => {
      return {
        key: item.id,
        fullName: item.fullName,
        entries: item.total,
        approved: item.approvedEntry,
        qcCount: item.qcCount
      };
    });
    return sortTableDataWithSelectedFirst(data, selectedUserIds);
  }, [userData, selectedUserIds]);

  const tableQcUserData = useMemo(() => {
    const data = qcUsersData.map((item: any) => {
      return {
        key: item.id,
        fullName: item.fullName,
        qcCount: item.qcCount || 0
      };
    });
    return sortTableDataWithSelectedFirst(data, selectedQCUserIds);
  }, [qcUsersData, selectedQCUserIds]);

  const tableImageUserData = useMemo(() => {
    const data = imageManagementData.map((item: any) => {
      return {
        key: item.id,
        fullName: item.fullName,
        imageCount: item.imageCount || 0
      };
    });
    return sortTableDataWithSelectedFirst(data, selectedImageUserIds);
  }, [imageManagementData, selectedImageUserIds]);

  const tableMappedUserData = useMemo(() => {
    const data = mappedItemsData.map((item: any) => {
      return {
        key: item.id,
        fullName: item.fullName,
        mappedCount: item.mappedCount || 0
      };
    });
    return sortTableDataWithSelectedFirst(data, selectedMappedUserIds);
  }, [mappedItemsData, selectedMappedUserIds]);

  return (
    <div className={styles.dashboard}>
      <div className={styles["dashboard-internal"]}>
        <div className={styles["dashboard-internal__total"]}>
          {cardData.map(({ icon, title, value, isClickable, route }, index) => (
            <DashboardCard 
              key={index} 
              icon={icon} 
              title={title} 
              value={`${value}`} 
              isClickable={isClickable}
              route={route}
            />
          ))}

          <div className={styles["dashboard-internal__datePicker"]}>
            <RangePicker
              format="DD/MM/YYYY"
              style={{ width: '100%', height: 40 }}
              allowClear
              value={selectedDate}
              onChange={(value) => {
                if (!value || !value[0] || !value[1]) {
                  setSelectedDate(null);
                } else {
                  setSelectedDate([value[0].startOf("day"), value[1].endOf("day")]);
                }
              }}
            />

            <div className={styles["dashboard-internal__info"]}>
              <div className={styles["dashboard-internal__last_updated"]}>
                <div>
                  Last Updated
                </div>
                <div className={styles["dashboard-internal__last_updated-time"]}>
                  {lastUpdated}
                </div>
              </div>
              <div className={styles["dashboard-internal__refresh"]}>
                <ReloadOutlined
                  style={{ fontSize: 16 }}
                  onClick={() => {
                    fetchDashboardSummary();
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles["dashboard-card"]}>
          <div className={styles["dashboard-card_item"]}>
            <DashboardSelectorCard
              columns={categoriesColumns}
              data={tableCategoryData}
              icon={<CategoriesCovered />}
              title="Categories Covered"
              selectOptions={categoryOptions}
              totalEntries={selectedCategorySum}
              onSelectChange={handleCategoryChange}
              selectedItems={selectedCategoryIds}
            />
          </div>

          <div className={styles["dashboard-card_item"]}>
            <DashboardSelectorCard
              columns={usersColumnsForQC}
              data={tableQcUserData}
              icon={<DashBoardUser />}
              title="Qc Users"
              selectOptions={qcUserOptions}
              totalEntries={selectedQcUserSum}
              approvedEntries={selectedUserApprovedMedicines}
              onSelectChange={handleQCUserChange}
              selectedItems={selectedQCUserIds}
              selectedUserQcCount={selectedUserQcCount}
            />
          </div>

          <div className={styles["dashboard-card_item"]}>
            <DashboardSelectorCard
              columns={imageManagementColumns}
              data={tableImageUserData}
              icon={<DashBoardUser />}
              title="Image Management"
              selectOptions={imageUserOptions}
              totalEntries={selectedImageUserSum}
              onSelectChange={handleImageUserChange}
              selectedItems={selectedImageUserIds}
            />
          </div>

          {!isGroceryPanel && (
            <div className={styles["dashboard-card_item"]}>
              <DashboardSelectorCard
                columns={mappedItemsColumns}
                data={tableMappedUserData}
                icon={<DashBoardUser />}
                title="Mapped Items"
                selectOptions={mappedUserOptions}
                totalEntries={selectedMappedUserSum}
                onSelectChange={handleMappedUserChange}
                selectedItems={selectedMappedUserIds}
              />
            </div>
          )}

          <div className={styles["dashboard-card_item"]}>
            <DashboardSelectorCard
              columns={usersColumns}
              data={tableUserData}
              icon={<DashBoardUser />}
              title="Users"
              selectOptions={userOptions}
              totalEntries={selectedUserSum}
              approvedEntries={selectedUserApprovedMedicines}
              onSelectChange={handleUserChange}
              selectedItems={selectedUserIds}
              selectedUserQcCount={selectedUserQcCount}
            />
          </div>
        </div>
        <div className={styles["dashboard-chart"]}>
          <ChartWithRangeSelector
            data={chartData}
            range={chartRange}
            onRangeChange={handleRangeChange}
            loading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardTable;
